import React, { useEffect, useRef, useState } from "react";
import { Col, Row, Button } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "react-toastify";
import parse from "react-html-parser";
import { useDispatch } from "react-redux";

import "./ResetPassword.css";

import { getRequestId, signIn, validateResetPwdToken } from "@/api/LoginSignup";
import { getUserProfile, updatePassword } from "@/api/myProfile";
import InputField from "@/components/common/InputField/InputField";
import PageFooter from "@/components/common/PageFooter";
import LoadedText from "@/components/loader/LoadedText";
import LoadingText from "@/components/loader/LoadingText";
import {
  trackApiCall,
  trackButtonClick,
  trackPageMount,
} from "@/utils/analytics";
import { encrypt } from "@/utils/crypto";
import { validateForm } from "@/utils/formValidator";
import { generateRandomID } from "@/utils/generateRandomID";
import { setProfileInfo } from "@/redux/user/userSlice";
import { ResetPasswordErrorMEssage } from "@/setting";
import { formatCmsTextToHtml } from "@/utils/textParser";

const successMsg =
  "Congratulations!! Your password has been successfully updated. Redirecting to HomePage";
const expiredLinkMsg =
  "Looks like the link has expired, please use the forgot password option again.";

const ResetPassword = () => {
  const formRefs = useRef({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const tokenId = searchParams.get("tokenId");

  const [isLoading, setIsLoading] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [isTokenValidating, setIsTokenValidating] = useState(true);
  const [linkExpired, setLinkExpired] = useState(false);
  const [blurredFields, setBlurredFields] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [errors, setErrors] = useState({
    newPassword: "",
    confirmPassword: "",
    apiError: "",
  });
  const [passwords, setPasswords] = useState({
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (tokenId) {
      const validateToken = async () => {
        try {
          setIsTokenValidating(true);
          const response = await validateResetPwdToken(tokenId);
          if (response === "VALIDATION_TOKEN_EXPIRED") {
            setLinkExpired(true);
          }
        } catch (error) {
          if (
            error?.errorMessage === "VALIDATION_TOKEN_EXPIRED" ||
            error?.message === "VALIDATION_TOKEN_EXPIRED" ||
            error?.response?.data?.errorMessage === "VALIDATION_TOKEN_EXPIRED"
          ) {
            setLinkExpired(true);
          } else {
            setLinkExpired(true);
          }
        } finally {
          setIsTokenValidating(false);
        }
      };
      validateToken();
    }
  }, [tokenId]);

  useEffect(() => {
    trackPageMount({
      pageName: "reset_password",
      eventCategory: "Reset Password",
      eventSource: "on page mount",
    });
  }, []);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const passwordFields = ["newPassword", "confirmPassword"];

    setBlurredFields((prev) => ({
      ...prev,
      [name]: true,
    }));

    // Check if the field is empty
    if (value === "" && passwordFields.includes(name)) {
      setErrors((prev) => ({
        ...prev,
        [name]: "Please enter your Password",
      }));
      return;
    }

    // If value is not empty, validate using validateForm
    if (value !== "" && passwordFields.includes(name)) {
      // Create a temporary object with just the current field to validate
      const fieldToValidate = { [name]: value };

      // Validate the single field
      const validationErrors = validateForm(
        fieldToValidate,
        [name],
        false,
        true
      );

      // Special case for confirmPassword - check if it matches newPassword
      if (
        name === "confirmPassword" &&
        passwords.newPassword &&
        value !== passwords.newPassword
      ) {
        validationErrors.confirmPassword =
          "New password and confirm password do not match";
      }

      // Update errors state if there are validation errors
      if (Object.keys(validationErrors).length > 0) {
        setErrors((prev) => ({
          ...prev,
          ...validationErrors,
        }));
      }
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;

    setPasswords((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setBlurredFields((prev) => ({
      ...prev,
      [name]: false,
    }));
  };

  const handleResetPassword = async () => {
    try {
      const validationErrors = validateForm(
        passwords,
        ["newPassword", "confirmPassword"], // For regular users
        false,
        true
      );

      if (
        passwords.newPassword &&
        passwords.confirmPassword &&
        passwords.newPassword !== passwords.confirmPassword
      ) {
        validationErrors.confirmPassword =
          "New password and confirm password do not match";
      }

      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }

      setErrors({
        newPassword: "",
        confirmPassword: "",
      });

      trackButtonClick({
        eventName: "reset_password_click",
        eventCategory: "Authentication",
        eventSource: "Reset password click",
      });

      setIsLoading(true);

      const requestId = generateRandomID();
      await getRequestId({ requestId: requestId });
      const encryptedNewPassword = await encrypt(passwords.newPassword);

      const payload = {
        cryptoAlgVersion: "V3",
        password: encryptedNewPassword,
        requestId: requestId,
        token: tokenId,
      };

      // const response = await changePassword(payload);
      const response = await updatePassword(payload);

      if (response.status === "SUCCESS") {
        setPasswordResetSuccess(true);

        trackApiCall({
          eventName: "reset_password_success",
          eventCategory: "Authentication",
          eventSource: "Reset password api call",
        });

        const userEmail = response?.data?.emailId;
        await handleLogin(userEmail);
      } else if (response.status === "FAILURE") {
        trackApiCall({
          eventName: "reset_password_failure",
          eventCategory: "Authentication",
          eventSource: "Reset password api call",
        });
        if (response.errorMessage.includes("May not be one of your previous")) {
          const text =
            "Password must be: Between 8 and 20 characters long. Include a combination of uppercase and lowercase letters, numbers, and at least one of the following special characters: ! # @ % $ ; ^ May not be one of your previous 12 passwords or contain your screen name.";
          setErrors((prev) => ({
            ...prev,
            apiError: text,
          }));
        } else {
          const errorCode = response?.errorMessage;
          const errorMessage =
            ResetPasswordErrorMEssage[errorCode] || response?.errorMessage;

          setErrors((prev) => ({
            ...prev,
            apiError: errorMessage,
          }));
        }
      }
    } catch (error) {
      console.log("Failed to update password");
      toast.error(error?.message || "Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (email) => {
    const payload = {
      requestId: generateRandomID(),
    };
    await getRequestId(payload);

    const redisPayload = new FormData();
    redisPayload.append("emailAddr", await encrypt(email));

    const loginFormdata = new FormData();
    loginFormdata.append("username", email);
    loginFormdata.append("password", await encrypt(passwords.newPassword));
    loginFormdata.append("requestId", generateRandomID());
    loginFormdata.append("cryptoAlgVersion", "V3");
    loginFormdata.append("encAlgVersion", "V4");

    const loginResponse = await signIn(loginFormdata);

    const { status, data } = await getUserProfile();
    dispatch(setProfileInfo(data));

    trackApiCall({
      eventName: "user_sign_in_success",
      eventSource: "Reset Password signin",
      eventCategory: "Sign-In",
    });

    setPasswords({
      newPassword: "",
      confirmPassword: "",
    });

    setBlurredFields({
      newPassword: false,
      confirmPassword: false,
    });

    trackApiCall({
      eventName: "reset_password_success",
      eventCategory: "Authentication",
      eventSource: "Reset password api call",
    });

    setTimeout(() => {
      navigate("/");
    }, 3000);
  };

  return (
    <div>
      {/* <!-- Main Header Nav --> */}

      <div className="container reset-password-container">
        <div className="mb-4 reset-password-text-container">
          <h3>Forgot your password</h3>
          <span className="reset-password-text-span">
            {!passwordResetSuccess && "Please create a new password"}
          </span>
        </div>

        {/* New Password */}
        {passwordResetSuccess && (
          <span className="reset-password-text-span">{successMsg}</span>
        )}

        {linkExpired && (
          <span className="reset-password-text-span">{expiredLinkMsg}</span>
        )}

        {isTokenValidating && (
          <div className="text-center">
            <LoadingText text="Validating token..." />
          </div>
        )}

        {!passwordResetSuccess && !linkExpired && !isTokenValidating && (
          <>
            <Row className="mb-3">
              <Col>
                <InputField
                  formRef={formRefs}
                  type="password"
                  placeholder="Password"
                  label="Password"
                  className="mb-0"
                  showToolTip={true}
                  width="62%"
                  required
                  value={passwords.newPassword}
                  name={"newPassword"}
                  key="newPassword"
                  onChange={handlePasswordChange}
                  handleBlur={handleBlur}
                  isEditButtonEnabled={false}
                  error={errors.newPassword}
                  fieldStyle={
                    blurredFields.newPassword &&
                    !errors.newPassword &&
                    passwords.newPassword
                      ? {
                          borderBottom: "2px solid rgb(49, 208, 4)",
                        }
                      : {}
                  }
                  lableStyle={{
                    fontFamily: "graphikregular",
                  }}
                />
              </Col>
            </Row>

            {/* Confirm Password */}
            <Row className="mb-3">
              <Col>
                <InputField
                  type="password"
                  showRightIcon={false}
                  label="Confirm Password"
                  className="mb-0"
                  placeholder="Password"
                  width="62%"
                  required
                  value={passwords.confirmPassword}
                  key="confirmPassword"
                  name={"confirmPassword"}
                  onChange={handlePasswordChange}
                  handleBlur={handleBlur}
                  isEditButtonEnabled={false}
                  error={errors.confirmPassword}
                  fieldStyle={
                    blurredFields.confirmPassword &&
                    !errors.confirmPassword &&
                    passwords.confirmPassword
                      ? {
                          borderBottom: "2px solid rgb(49, 208, 4)",
                        }
                      : {}
                  }
                  lableStyle={{
                    fontFamily: "graphikregular",
                  }}
                />
              </Col>
            </Row>

            {/* Update Button */}
            <Row
              className="mb-5 align-items-center"
              style={{ paddingLeft: "21%" }}
            >
              <Col sm={10}>
                <Button
                  className="btn btn-primary reset-password-submit-btn"
                  onClick={handleResetPassword}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <LoadingText text="Processing..." />
                  ) : (
                    <LoadedText text="Submit" />
                  )}
                </Button>
              </Col>
            </Row>

            {/* API Error Display */}
            {errors.apiError && (
              <Row className="mb-3">
                <Col>
                  <div
                    className="text-danger"
                    style={{
                      fontSize: "14px",
                      fontFamily: "graphikregular",
                      width: "70%",
                    }}
                  >
                    {parse(formatCmsTextToHtml(errors.apiError))}
                  </div>
                </Col>
              </Row>
            )}
          </>
        )}
      </div>
      <PageFooter showPartners={false} />
    </div>
  );
};

export default ResetPassword;
